// Typed API layer for the CRM ticket endpoints consumed by the admin SPA
// (Fase 2 — LeadTickets panel; Fase 4 — global Tickets tab + ticket detail).
// Thin wrappers over crmRequest so views never inline paths and share one place
// for the ticket contract.

import { crmDownload, crmFetchBlobUrl, crmRequest } from "./crm";
import { canonicalLeadIdOrNull } from "./crmLeadId";
import {
  AssignTicketBody,
  CreateTicketBody,
  CreateTicketCommentBody,
  CreateTicketResponse,
  MoveTicketBody,
  MoveTicketPipelineBody,
  TicketActivityEntry,
  TicketComment,
  TicketDetail,
  TicketPipeline,
  TicketPriority,
  TicketsBoardFilters,
  TicketsBoardResponse,
  TicketsListFilters,
  TicketStage,
  TicketSummary,
  TicketsListResponse,
} from "./crmTypes";

// Shared ticket-domain labels/formatters so the list tab and the detail view
// render the same Spanish copy from one source (was duplicated in both views).

// Human-readable priority labels (es-ES).
export const PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: "Baja",
  medium: "Media",
  high: "Alta",
  urgent: "Urgente",
};

// Summarise an activity-log entry into readable text. `type` is a closed union;
// `action` is free text on the CRM side, so we surface it verbatim when present.
// `pipelineLabels` (id → label) es opcional: las entradas 'pipeline_moved'
// guardan IDs de pipeline en `detail` y sin el mapa pintaríamos IDs crudos.
export function describeActivity(
  e: TicketActivityEntry,
  pipelineLabels?: Record<string, string>,
): string {
  if (e.type === "stage") {
    return `${e.actor} movió de "${e.fromStage ?? "—"}" a "${e.toStage ?? "—"}"`;
  }
  if (e.type === "assignment") {
    return `${e.actor} reasignó de "${e.fromAssignee ?? "sin asignar"}" a "${
      e.toAssignee ?? "sin asignar"
    }"`;
  }
  if (e.action === "pipeline_moved") {
    // detail = { fromPipeline, toPipeline } con IDs (tickets.repo.ts del CRM).
    const label = (v: unknown) =>
      typeof v === "string" ? pipelineLabels?.[v] ?? v : "—";
    return `${e.actor} movió el ticket del pipeline "${label(
      e.detail?.fromPipeline,
    )}" al "${label(e.detail?.toPipeline)}"`;
  }
  return `${e.actor}: ${e.action ?? "acción"}`;
}

// El CRM devuelve `leadId` en hex-32 interno; el admin-api de Operations solo
// acepta el ULID canónico → normalizamos en la capa API para que las vistas
// (Tickets, board, detalle) linken a la ficha sin dar 400. Los helpers son
// tolerantes: canónico intacto, null intacto. El leadId que ENVIAMOS como filtro
// no se toca (el CRM acepta ambas formas en input).
function normalizeTicketSummary<T extends TicketDetail | TicketSummary>(t: T): T {
  return { ...t, leadId: canonicalLeadIdOrNull(t.leadId) };
}

// GET /tickets/payment-issues — tickets de impago/disputa que la vista Cobranza
// (Operations) cruza en cliente con el estado de cobro de Stripe. `months` es una
// lista 'YYYY-MM,YYYY-MM,...' (las 4 columnas del calendario) para que el CRM
// acote la ventana. Devuelve el leadId en hex-32 interno del CRM; lo
// canonicalizamos a ULID para poder cruzarlo con el leadUlid del admin-api.
export function listPaymentIssueTickets(
  months: string[],
): Promise<import("./crmTypes").PaymentIssuesResponse> {
  const params = new URLSearchParams();
  if (months.length > 0) params.set("months", months.join(","));
  const qs = params.toString();
  return crmRequest<import("./crmTypes").PaymentIssuesResponse>(
    `/tickets/payment-issues${qs ? `?${qs}` : ""}`,
  ).then((res) => ({
    ...res,
    items: res.items.map((t) => ({
      ...t,
      leadId: canonicalLeadIdOrNull(t.leadId) ?? t.leadId,
    })),
  }));
}

// GET /tickets — filtered, paginated list. Every filter is optional; the query
// string is built with URLSearchParams (never concatenation) so encoding and
// empty-value handling are consistent. Never 404s: an unknown/unprojected lead
// or an over-restrictive filter just yields { items: [], total: 0 }.
//
// OJO con `archived`: omitirlo NO es "sin filtro", es 'exclude' (solo vivos).
// Un listado que deba seguir alcanzando un archivado (la ficha del lead) tiene
// que pedir 'include' explícitamente — ver LeadTickets.
export function listTickets(
  filters: TicketsListFilters,
): Promise<TicketsListResponse> {
  const params = new URLSearchParams();
  if (filters.leadId) params.set("leadId", filters.leadId);
  if (filters.pipelineId) params.set("pipelineId", filters.pipelineId);
  if (filters.stageId) params.set("stageId", filters.stageId);
  if (filters.state) params.set("state", filters.state);
  if (filters.assignedTo) params.set("assignedTo", filters.assignedTo);
  if (filters.market) params.set("market", filters.market);
  // Sin esta línea el `archived` del llamante se perdería en silencio (esto es
  // una lista blanca) y el CRM aplicaría su default 'exclude'.
  if (filters.archived) params.set("archived", filters.archived);
  if (filters.incidentType) params.set("incidentType", filters.incidentType);
  if (filters.typification) params.set("typification", filters.typification);
  if (filters.q) params.set("q", filters.q);
  if (filters.page !== undefined) params.set("page", String(filters.page));
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  const qs = params.toString();
  return crmRequest<TicketsListResponse>(`/tickets${qs ? `?${qs}` : ""}`).then(
    (res) => ({ ...res, items: res.items.map(normalizeTicketSummary) }),
  );
}

// GET /tickets/:id -> TicketDetail (with hydrated lead). 404 if the ticket does
// not exist.
export function getTicket(id: string): Promise<TicketDetail> {
  return crmRequest<TicketDetail>(`/tickets/${encodeURIComponent(id)}`).then(
    (t) => {
      const norm = normalizeTicketSummary(t);
      // El lead hidratado trae su propio `id` (también hex-32): normalízalo para
      // que el link "ver el lead" del detalle no dé 400.
      return norm.lead
        ? {
            ...norm,
            lead: { ...norm.lead, id: canonicalLeadIdOrNull(norm.lead.id) ?? norm.lead.id },
          }
        : norm;
    },
  );
}

// GET /tickets/pipelines — all pipelines (caller filters archived).
export function listTicketPipelines(): Promise<TicketPipeline[]> {
  return crmRequest<TicketPipeline[]>("/tickets/pipelines");
}

// Caché de la lista de pipelines (mismo racional y TTL que la de stages de más
// abajo): cada TicketPipelineSelect monta su propio fetch, así que el panel de
// tickets del lead pediría los pipelines una vez POR FILA. Se cachea la promesa
// para que N montajes concurrentes compartan una única petición en vuelo.
const PIPELINES_TTL_MS = 5 * 60 * 1000;
let pipelinesCache: { at: number; promise: Promise<TicketPipeline[]> } | null =
  null;

// Pipelines activos, ordenados por posición y cacheados. Misma forma para todo
// consumidor: sin archivados y con orden estable.
export function loadTicketPipelines(): Promise<TicketPipeline[]> {
  if (pipelinesCache && Date.now() - pipelinesCache.at < PIPELINES_TTL_MS) {
    return pipelinesCache.promise;
  }
  const promise = listTicketPipelines().then((ps) =>
    ps.filter((p) => !p.archived).sort((a, b) => a.position - b.position),
  );
  const entry = { at: Date.now(), promise };
  pipelinesCache = entry;
  // Un fallo no debe quedar cacheado 5 min (mismo guard que loadTicketStages).
  promise.catch(() => {
    if (pipelinesCache === entry) pipelinesCache = null;
  });
  return promise;
}

// GET /tickets/pipelines/:id/stages — stages of a pipeline (caller filters
// archived).
export function listTicketStages(pipelineId: string): Promise<TicketStage[]> {
  return crmRequest<TicketStage[]>(
    `/tickets/pipelines/${encodeURIComponent(pipelineId)}/stages`,
  );
}

// Caché de stages por pipeline (TTL 5 min, mismo staleTime que el
// `useTicketStages` del CRM). Este SPA no usa react-query, así que sin caché el
// panel de tickets del lead pediría los stages una vez POR FILA (cada
// TicketStageSelect monta su propio fetch). Cacheamos la PROMESA, no el
// resultado: N filas montadas a la vez en el mismo pipeline comparten una única
// petición en vuelo. Los stages son datos de configuración, casi inmutables.
const STAGES_TTL_MS = 5 * 60 * 1000;
const stagesCache = new Map<string, { at: number; promise: Promise<TicketStage[]> }>();

// Stages activos de un pipeline, ordenados por posición y cacheados. El BFF ya
// filtra `archived` y ordena server-side (tickets.repo.ts); lo repetimos aquí
// para que todo consumidor reciba la misma forma sin volver a filtrar.
export function loadTicketStages(pipelineId: string): Promise<TicketStage[]> {
  const hit = stagesCache.get(pipelineId);
  if (hit && Date.now() - hit.at < STAGES_TTL_MS) return hit.promise;

  const promise = listTicketStages(pipelineId).then((ss) =>
    ss.filter((s) => !s.archived).sort((a, b) => a.position - b.position),
  );
  stagesCache.set(pipelineId, { at: Date.now(), promise });
  // Un fallo no debe quedar cacheado 5 min: purga la entrada para que el
  // siguiente montaje (o un "Reintentar") vuelva a pedirlo. El guard evita
  // borrar una entrada más nueva que ya reemplazó a esta.
  promise.catch(() => {
    if (stagesCache.get(pipelineId)?.promise === promise) {
      stagesCache.delete(pipelineId);
    }
  });
  return promise;
}

// GET /tickets/:id/comments -> { items } — ticket-scoped notes.
export function listTicketComments(id: string): Promise<TicketComment[]> {
  return crmRequest<{ items: TicketComment[] }>(
    `/tickets/${encodeURIComponent(id)}/comments`,
  ).then((res) => res.items);
}

// POST /tickets/:id/comments -> 201 { id }.
export function addTicketComment(
  id: string,
  body: CreateTicketCommentBody,
): Promise<CreateTicketResponse> {
  return crmRequest<CreateTicketResponse>(
    `/tickets/${encodeURIComponent(id)}/comments`,
    { method: "POST", body },
  );
}

// PATCH /tickets/:id/comments/:commentId/pin -> { pinned }. Chincheta de una
// nota del ticket (roles admin|ops|sales — fijar es acción de EQUIPO, no exige
// ser el autor). Es la MISMA columna que el pin del timeline del lead: fijar
// aquí también la fija en la ficha del lead asociado, y viceversa. 404
// (not_found) si la nota no pertenece al ticket.
export function setTicketCommentPinned(
  ticketId: string,
  commentId: string,
  pinned: boolean,
): Promise<{ pinned: boolean }> {
  return crmRequest<{ pinned: boolean }>(
    `/tickets/${encodeURIComponent(ticketId)}/comments/${encodeURIComponent(
      commentId,
    )}/pin`,
    { method: "PATCH", body: { pinned } },
  );
}

// DELETE /tickets/:id/comments/:commentId -> 204. BORRADO LÓGICO de una nota
// del ticket: la fila se conserva (deletedAt/deletedBy) y queda rastro
// note_deleted en el historial. Autorización fina en el CRM: solo el AUTOR o
// un admin (403 si no). Los admins siguen viendo la nota borrada en el listado
// (marcada) y pueden restaurarla.
export function deleteTicketComment(
  ticketId: string,
  commentId: string,
): Promise<void> {
  return crmRequest<void>(
    `/tickets/${encodeURIComponent(ticketId)}/comments/${encodeURIComponent(
      commentId,
    )}`,
    { method: "DELETE" },
  );
}

// POST /tickets/:id/comments/:commentId/restore -> 204. Restaura una nota
// borrada (solo admin en el CRM; deja rastro note_restored en el historial).
export function restoreTicketComment(
  ticketId: string,
  commentId: string,
): Promise<void> {
  return crmRequest<void>(
    `/tickets/${encodeURIComponent(ticketId)}/comments/${encodeURIComponent(
      commentId,
    )}/restore`,
    { method: "POST" },
  );
}

// GET /tickets/:id/attachments/:attachmentId/download -> stream binario con
// Content-Disposition (descarga autenticada por cookie; el blob vive en un
// bucket privado GCS, nunca URL pública). Roles admin|ops|sales en el CRM.
export function downloadTicketAttachment(
  ticketId: string,
  attachmentId: string,
  filename: string,
): Promise<void> {
  return crmDownload(
    `/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(
      attachmentId,
    )}/download`,
    filename,
  );
}

// GET /tickets/:id/attachments/:attachmentId/download -> objectURL del blob
// (mismo endpoint autenticado que la descarga, pero para pintar la imagen
// inline con <img src>). El llamante revoca el objectURL al desmontar.
export function fetchTicketAttachmentObjectUrl(
  ticketId: string,
  attachmentId: string,
): Promise<string> {
  return crmFetchBlobUrl(
    `/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(
      attachmentId,
    )}/download`,
  );
}

// GET /tickets/:id/activity -> { items } — audit log of the ticket.
export function getTicketActivity(
  id: string,
): Promise<TicketActivityEntry[]> {
  return crmRequest<{ items: TicketActivityEntry[] }>(
    `/tickets/${encodeURIComponent(id)}/activity`,
  ).then((res) => res.items);
}

// GET /tickets/board — kanban of one pipeline: columns (stages) each carrying up
// to `limitPerColumn` tickets. `pipelineId` is required; the query string is
// built with URLSearchParams. 404 (pipeline_not_found) if the pipeline is
// unknown — the board view surfaces that as "pipeline sin stages / no existe".
export function getBoard(
  filters: TicketsBoardFilters,
): Promise<TicketsBoardResponse> {
  const params = new URLSearchParams();
  params.set("pipelineId", filters.pipelineId);
  if (filters.market) params.set("market", filters.market);
  if (filters.assignedTo) params.set("assignedTo", filters.assignedTo);
  if (filters.limitPerColumn !== undefined) {
    params.set("limitPerColumn", String(filters.limitPerColumn));
  }
  return crmRequest<TicketsBoardResponse>(
    `/tickets/board?${params.toString()}`,
  ).then((res) => ({
    ...res,
    columns: res.columns.map((col) => ({
      ...col,
      tickets: col.tickets.map(normalizeTicketSummary),
    })),
  }));
}

// POST /tickets/:id/move -> 204. Moves the ticket to `stageId` (same pipeline).
// The CRM returns 400 (invalid_stage) if the stage does not exist or belongs to
// another pipeline. NOTE: a move on a ticket that no longer exists resolves 204
// silently (the CRM early-returns), so the board reconciles by refreshing on the
// next load rather than trusting the 204 alone.
export function moveTicket(id: string, body: MoveTicketBody): Promise<void> {
  return crmRequest<void>(`/tickets/${encodeURIComponent(id)}/move`, {
    method: "POST",
    body,
  });
}

// POST /tickets/:id/move-pipeline -> 204. Moves the ticket to ANOTHER pipeline
// (roles admin|ops). Without `stageId` the CRM lands it on the destination's
// first open stage; the market follows the destination pipeline. Queda doble
// rastro en el historial: movimiento de stage + entrada 'pipeline_moved'.
export function moveTicketPipeline(
  id: string,
  body: MoveTicketPipelineBody,
): Promise<void> {
  return crmRequest<void>(`/tickets/${encodeURIComponent(id)}/move-pipeline`, {
    method: "POST",
    body,
  });
}

// POST /tickets/:id/assign -> 204. Sets the assignee (a valid email) or null to
// unassign; the CRM enforces the email format (400 if malformed). Like move, an
// assign on a vanished ticket resolves 204 silently.
export function assignTicket(
  id: string,
  body: AssignTicketBody,
): Promise<void> {
  return crmRequest<void>(`/tickets/${encodeURIComponent(id)}/assign`, {
    method: "POST",
    body,
  });
}

// PATCH /tickets/:id -> 204. Edita subject (1..500), description (<=10000,
// nullable) y/o tipificación (incidentType/typification, nullable = quitar; el
// backend valida la Opción 2 contra el pipeline del ticket). Roles admin|ops. Al
// menos un campo debe venir.
export function updateTicket(
  id: string,
  body: {
    subject?: string;
    description?: string | null;
    incidentType?: string | null;
    typification?: string | null;
  },
): Promise<void> {
  return crmRequest<void>(`/tickets/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body,
  });
}

// POST /tickets/:id/archive -> 204. El CRM no borra tickets: se ARCHIVAN
// (desaparecen de listados y board; queda rastro en el activity log). Roles
// admin|ops. archived:false los restaura.
export function setTicketArchived(id: string, archived: boolean): Promise<void> {
  return crmRequest<void>(`/tickets/${encodeURIComponent(id)}/archive`, {
    method: "POST",
    body: { archived },
  });
}

// POST /tickets -> 201 { id }.
export function createTicket(
  body: CreateTicketBody,
): Promise<CreateTicketResponse> {
  return crmRequest<CreateTicketResponse>("/tickets", {
    method: "POST",
    body,
  });
}
